import { createSlice } from '@reduxjs/toolkit'
import { v1 as uuid } from 'uuid'
import { QUESTION_TYPES } from '../../components/QuestionTypes'
import { INITIAL_QUESTIONNAIRE_CONTEXT } from '../../utils'
import structuredClone from 'core-js-pure/actual/structured-clone'

const reorderList = (list, startIndex, endIndex) => {
  let newList = list.slice(0, list.length)
  const [removed] = newList.splice(startIndex, 1)
  newList.splice(endIndex, 0, removed)
  return newList
}

const reorder = (list, source, destination) => {
  let newList
  // There are 4 possible cases:
  // 1) We are ordering elements within the parent list, in which case we
  //    simply need to reorder the elements and return the new list
  // 2) We are ordering elements within a nested list, in which case we need
  //    to reorder the elements within the list, and update the reference in
  //    the parent list with the new list
  // 3) We are moving an element from the parent list to a nested list, in
  //    which case we need to remove the element from the parent list and add
  //    it to the nested list at the correct location
  // 4) We are moving an element from the nested list to the parent list, in
  //    which case we need to remove the element from the nested list and add
  //    it to the parent list at the correct location

  // Case 1, within the parent list
  if (
    source.parentIndex === destination.parentIndex &&
    source.parentIndex === null
  ) {
    // Note that reorderList will return a copy of the list with the new order
    newList = reorderList(list, source.index, destination.index)
  }
  // Case 2, within a nested list
  else if (
    source.parentIndex === destination.parentIndex &&
    source.parentIndex !== null
  ) {
    // No need to copy the list, since we are not modifying it directly
    newList = list
    // We do need to copy the nested list though
    newList[source.parentIndex] = structuredClone(newList[source.parentIndex])
    newList[source.parentIndex].content = reorderList(
      newList[source.parentIndex].content,
      source.index,
      destination.index
    )
  }
  // Case 3, from parent to nested
  else if (source.parentIndex === null && destination.parentIndex !== null) {
    newList = [...list]
    const [removed] = newList.splice(source.index, 1)
    let targetIndex = destination.parentIndex

    // At this point we have already deleted the source from newList.
    // If we started the drag from a source above the intended destination,
    // the destination index in newList is one less
    if (source.index < destination.parentIndex) {
      targetIndex -= 1
    }
    newList[targetIndex] = structuredClone(newList[targetIndex])
    newList[targetIndex].content.splice(destination.index, 0, removed)
  }
  // Case 4, from nested to parent
  else if (source.parentIndex !== null && destination.parentIndex === null) {
    newList = [...list]
    newList[source.parentIndex] = structuredClone(newList[source.parentIndex])
    const [removed] = newList[source.parentIndex].content.splice(
      source.index,
      1
    )
    newList.splice(destination.index, 0, removed)
  }
  // There is an extra case, if drag is from nested to nested. This is extremely unlikely though, since it's hard to
  // go from nested to nested without hovering over parent first, so we choose not to implement it.
  else {
    console.warn('Dragging from nested to nested is not supported')
  }

  return newList
}

const initialTextOptionsEnglish = [
  'option 1',
  'option 2',
  'option 3',
  'option 4',
]

const initialTextOptionsDutch = ['optie 1', 'optie 2', 'optie 3', 'optie 4']

const copy = (source, destination, label, target, destinationIndex) => {
  let newDestination
  let targetIndex
  if (target === null) {
    newDestination = destination.slice(0, destination.length)
  } else {
    targetIndex = destination.findIndex((q) => q.id === target)
    const targetContent = destination[targetIndex]?.content || []
    newDestination = targetContent.slice(0, targetContent.length)
  }

  // initialising question JSONs.
  switch (label) {
    case 'range':
      newDestination.splice(destinationIndex, 0, {
        id: uuid(),
        type: label,
        title: { en: `untitled ${label}`, nl: `naamloos ${label}` }, // TODO: idk if dutch version is gramatically correct here
        min: '0',
        max: '100',
        step: '1',
        labels: initialTextOptionsEnglish.map((english, index) => ({
          en: english,
          nl: initialTextOptionsDutch[index],
        })),
      })
      break
    case 'checkbox':
    case 'radio':
    case 'likert':
    case 'dropdown':
      newDestination.splice(destinationIndex, 0, {
        id: uuid(),
        type: label,
        title: { en: `untitled ${label}`, nl: `naamloos ${label}` }, // TODO: translate untitled to dutch
        options: initialTextOptionsEnglish.map((english, index) => ({
          title: {
            en: english,
            nl: initialTextOptionsDutch[index],
          },
        })),
      })
      break
    case 'raw':
      newDestination.splice(destinationIndex, 0, {
        id: uuid(),
        type: label,
        content: '<p>This is an untitled raw question</p>',
      })
      break
    default:
      newDestination.splice(destinationIndex, 0, {
        id: uuid(),
        type: label,
        title: { en: `untitled ${label}`, nl: `naamloos ${label}` },
      })
      break
  }

  if (target !== null) {
    const updatedSubquestions = newDestination
    newDestination = destination.slice(0, destination.length)
    newDestination[targetIndex] = structuredClone(newDestination[targetIndex])
    newDestination[targetIndex].content = updatedSubquestions
  }

  return newDestination
}

// performs a deep copy of the question to ensure that it can be safely edited
// without modifying the original question, and recreates some values that
// should not be copied over to the new question (i.e. uuids)
const cloneAndCleanUpQuestion = (question) => {
  const clone = structuredClone(question)
  clone.id = uuid()

  if (question.type === 'expandable') {
    clone.content.forEach((subquestion) => {
      subquestion.id = uuid()
    })
  }

  return clone
}

const duplicateQuestion = (action, state) => {
  const newState = [...state]
  for (const [index, question] of newState.entries()) {
    if (question.id === action.question.id) {
      newState.push(cloneAndCleanUpQuestion(action.question))
      break
    }

    if (question.type === 'expandable') {
      const newContent = duplicateQuestion(action, question.content)

      if (newContent.length !== question.content.length) {
        newState[index] = { ...question, content: newContent }
        // The question was found at the sublevel and has already been copied. We can stop.
        break
      }
    }
  }

  return newState
}

const updateQuestion = (payload, state) => {
  const newState = [...state]
  for (const [index, question] of newState.entries()) {
    if (question.id === payload.id) {
      newState[index] = payload.new
      break
    }

    if (question.type === 'expandable') {
      // This copy is important to avoid mutation of current state references (which is illegal in redux)
      newState[index] = structuredClone(newState[index])
      newState[index].content = updateQuestion(payload, question.content)
    }
  }

  return newState
}

const removeQuestion = (payload, state) => {
  const newState = []
  for (const question of state) {
    if (question.id !== payload.id) {
      // copy the contents
      newState.push({ ...question })

      if (question.type === 'expandable') {
        newState[newState.length - 1].content = removeQuestion(
          payload,
          question.content || []
        )
      }
    }
  }

  return newState
}

export const questionsSlice = createSlice({
  name: 'questions',
  initialState: INITIAL_QUESTIONNAIRE_CONTEXT,
  reducers: {
    REORDER: (state, action) => {
      return reorder(state, action.payload.source, action.payload.destination)
    },
    // This is called when a question type is dragged from the toolbox to the question list to add a new question
    CLONE: (state, action) => {
      return copy(
        QUESTION_TYPES,
        state,
        action.payload.source,
        action.payload.destination.target,
        action.payload.destination.index
      )
    },
    SET_QUESTIONS: (state, action) => {
      return action.payload.questions
    },
    DUPLICATE_QUESTION: (state, action) => {
      return duplicateQuestion(action.payload, state)
    },
    REMOVE_QUESTION: (state, action) => {
      return removeQuestion(action.payload, state)
    },
    REMOVE_ALL: (state, action) => {
      return []
    },
    UPDATE_QUESTION: (state, action) => {
      return updateQuestion(action.payload, state)
    },
    REMOVE_BY_MAP: (state, action) => {
      const { type, key, qid, oid } = action.payload
      const stateLength = state.length
      for (let i = 0; i < stateLength; i++) {
        const question = state[i]
        if (question.id === qid) {
          const options = question.options
          if (options && options.length > 0) {
            for (let j = 0; j < options.length; j++) {
              const option = options[j]
              if (option.id === oid) {
                if (type === 'showsMap') {
                  state[i].options[j].shows_questions = state[i].options[
                    j
                  ].shows_questions.filter((x) => x !== key)
                } else if (type === 'hidesMap') {
                  state[i].options[j].hides_questions = state[i].options[
                    j
                  ].hides_questions.filter((x) => x !== key)
                }
                break
              }
            }
          }
          break
        }
      }
    },
  },
})

export const {
  REORDER,
  CLONE,
  SET_QUESTIONS,
  DUPLICATE_QUESTION,
  REMOVE_QUESTION,
  REMOVE_ALL,
  UPDATE_QUESTION,
  REMOVE_BY_MAP,
} = questionsSlice.actions

export default questionsSlice.reducer
